import type {
  Channel,
  Coverage,
  PolicyHolder,
  ProductType,
  Quote,
  WebShopPublicUser,
} from "@/api_client";

import { filterEventKeys } from "../utils/filterEventKeys";
import { quoteIndicatesUserMoving } from "../utils/quoteIndicatesUserMoving";
import customer from "./data/customer";
import environmentInfo from "./data/environmentInfo";
import insuranceInfo from "./data/insuranceInfo";
import objectInfo from "./data/objectInfo";
import { getPageInfo } from "./data/pageInfo";
import processInfo from "./data/processInfo";
import productInfo from "./data/productInfo";
import type {
  IEvent,
  PageLoad,
  PageLoadEventType,
  PageLoadMultipleProducts,
  PageType,
  ProcessStepName,
} from "./types/types";

export interface PageLoadedParams {
  pageType: PageType;
  pageLoadEventType: PageLoadEventType;
  productType?: ProductType;
  product?: Quote;
  enigmatizedProduct?: Quote;
  policyHolder?: PolicyHolder;
  processStepName?: ProcessStepName;
  language: string;
  user?: WebShopPublicUser;
  channel: Channel;
  enigmatizedPolicyHolder: PolicyHolder | undefined | null;
  /**
   * Select which properties to include in the emitted PageLoad event.
   * Always includes the mandatory 'event' field. If omitted or empty, all default fields are included.
   * Example: { includedKeys: ["customerInfo", "environmentInfo"] }
   */
  includedKeys?: readonly (keyof PageLoad)[];
}

export interface PageLoadedMultipleProductsParams {
  contextId: string;
  pageType: PageType;
  pageLoadEventType: PageLoadEventType;
  products: Quote[];
  enigmatizedProducts: Record<string, Quote>;
  coverageContent: Record<string, Coverage>;
  processStepName?: ProcessStepName;
  language: string;
  user?: WebShopPublicUser;
  channel: Channel;
  enigmatizedPolicyHolder: PolicyHolder | undefined | null;
  /**
   * Select which properties to include in the emitted PageLoad event.
   * Always includes the mandatory 'event' field. If omitted or empty, all default fields are included.
   * Example: { includedKeys: ["customerInfo", "environmentInfo"] }
   */
  includedKeys?: readonly (keyof PageLoad)[];
}

export const createPageLoad = ({
  pageType,
  pageLoadEventType,
  productType,
  product,
  enigmatizedPolicyHolder,
  enigmatizedProduct,
  processStepName,
  language,
  user,
  channel,
  includedKeys,
}: PageLoadedParams): IEvent => {
  const objectInfoItem =
    product &&
    objectInfo.getObjectInfo(
      enigmatizedPolicyHolder,
      product,
      enigmatizedProduct,
    );
  const moving = quoteIndicatesUserMoving([product]);
  const fullEvent: PageLoad = {
    event: pageLoadEventType,
    environmentInfo: environmentInfo.getEnvironmentInfo(channel),
    pageInfo: getPageInfo(pageType, globalThis.location.href, language),
    processInfo: processStepName && processInfo.getProcessInfo(processStepName),
    productInfo:
      productType && productInfo.getProductInfoEntryPage(productType, channel),
    customerInfo: customer.getCustomerInfo(
      enigmatizedPolicyHolder,
      user,
      moving,
    ),
    objectInfo: objectInfoItem ? [objectInfoItem] : [],
  };
  return filterEventKeys(fullEvent, includedKeys) as IEvent;
};

export const createPageLoadMultipleProducts = ({
  contextId,
  pageType,
  pageLoadEventType,
  products,
  enigmatizedPolicyHolder,
  enigmatizedProducts,
  processStepName,
  language,
  user,
  channel,
  includedKeys,
  coverageContent,
}: PageLoadedMultipleProductsParams): IEvent => {
  const committedProducts = products?.filter((p) => !!p.committedAt);
  const objectInfoItems = committedProducts.map((product) =>
    objectInfo.getObjectInfo(
      enigmatizedPolicyHolder,
      product,
      product.id ? enigmatizedProducts[product.id] : undefined,
    ),
  );
  const productInfos = committedProducts
    ? committedProducts.map((p) => productInfo.getProductInfo(p, channel))
    : undefined;
  const insuranceInfos =
    committedProducts && enigmatizedPolicyHolder
      ? committedProducts.map((product) =>
          insuranceInfo.getInsuranceInfo(contextId, product, coverageContent),
        )
      : [];
  const moving = quoteIndicatesUserMoving(committedProducts);
  const fullEvent: PageLoadMultipleProducts = {
    event: pageLoadEventType,
    environmentInfo: environmentInfo.getEnvironmentInfo(channel),
    pageInfo: getPageInfo(pageType, globalThis.location.href, language),
    processInfo: processStepName && processInfo.getProcessInfo(processStepName),
    productInfo: productInfos,
    customerInfo: customer.getCustomerInfo(
      enigmatizedPolicyHolder,
      user,
      moving,
    ),
    objectInfo: objectInfoItems,
    insuranceInfo: insuranceInfos,
  };
  return filterEventKeys(fullEvent, includedKeys) as IEvent;
};
